import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Toggle } from "../components/shared/Toggle";
import { Modal } from "../components/shared/Modal";
import { Spinner } from "../components/shared/Spinner";
import { categoriesAPI } from "../services/api";
import { useTitle } from "../hooks/useTitle";

export const CategoriesPage: React.FC = () => {
	useTitle("Categories");
	const qc = useQueryClient();
	const { data, isLoading } = useQuery({
		queryKey: ["cats"],
		queryFn: () => categoriesAPI.getAll(),
	});
	const cats = data?.data || [];
	const [modal, setModal] = useState(false);
	const [sel, setSel] = useState<any>(null);
	const [form, setForm] = useState({ name: "", icon: "🍽️", color: "#06a384" });

	const saveMut = useMutation({
		mutationFn: (d: any) =>
			sel ? categoriesAPI.update(sel.id, d) : categoriesAPI.create(d),
		onSuccess: () => {
			toast.success(sel ? "Updated" : "Created");
			qc.invalidateQueries({ queryKey: ["cats"] });
			setModal(false);
		},
	});
	const toggleMut = useMutation({
		mutationFn: (c: any) => categoriesAPI.update(c.id, { active: !c.active }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["cats"] }),
	});
	const delMut = useMutation({
		mutationFn: categoriesAPI.delete,
		onSuccess: () => {
			toast.success("Deleted");
			qc.invalidateQueries({ queryKey: ["cats"] });
		},
	});

	if (isLoading) return <Spinner />;

	return (
		<div>
			<div className="page-title">Menu Categories</div>
			<div className="page-sub">
				{cats.length} categories · {cats.filter((c: any) => c.active).length}{" "}
				active
			</div>

			<div className="card">
				<div className="card-title">
					All Categories
					<button
						className="btn-primary"
						onClick={() => {
							setSel(null);
							setForm({ name: "", icon: "🍽️", color: "#06a384" });
							setModal(true);
						}}>
						+ Add Category
					</button>
				</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
						gap: 10,
					}}>
					{cats.map((c: any) => (
						<div
							key={c.id}
							style={{
								background: "var(--card2)",
								borderRadius: "var(--r)",
								border: `1.5px solid ${c.active ? `${c.color}33` : "var(--border)"}`,
								padding: 16,
								position: "relative",
							}}>
							<div
								style={{
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									height: 3,
									background: c.color,
									borderRadius: "var(--r) var(--r) 0 0",
								}}
							/>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "flex-start",
									marginBottom: 8,
								}}>
								<span style={{ fontSize: 32 }}>{c.icon}</span>
								<Toggle on={c.active} onChange={() => toggleMut.mutate(c)} />
							</div>
							<div
								style={{
									fontFamily: "Syne,sans-serif",
									fontWeight: 800,
									fontSize: 15,
									marginBottom: 4,
								}}>
								{c.name}
							</div>
							<div
								style={{
									fontSize: 12,
									color: "var(--muted)",
									marginBottom: 12,
								}}>
								Sort: {c.sort}
							</div>
							<div style={{ display: "flex", gap: 6 }}>
								<button
									className="btn-secondary"
									style={{ flex: 1 }}
									onClick={() => {
										setSel(c);
										setForm({ name: c.name, icon: c.icon, color: c.color });
										setModal(true);
									}}>
									✏️ Edit
								</button>
								<button
									className="btn-secondary btn-danger"
									onClick={() => {
										if (window.confirm("Delete category?")) delMut.mutate(c.id);
									}}>
									🗑
								</button>
							</div>
						</div>
					))}
				</div>
			</div>

			{modal && (
				<Modal
					title={sel ? "Edit Category" : "New Category"}
					onClose={() => setModal(false)}
					onConfirm={() => saveMut.mutate(form)}
					confirmText={sel ? "Save" : "Create"}>
					<div className="form-row cols-2">
						<div className="form-group">
							<label className="form-label">Name</label>
							<input
								className="form-input"
								value={form.name}
								onChange={(e) =>
									setForm((f) => ({ ...f, name: e.target.value }))
								}
							/>
						</div>
						<div className="form-group">
							<label className="form-label">Icon (emoji)</label>
							<input
								className="form-input"
								value={form.icon}
								onChange={(e) =>
									setForm((f) => ({ ...f, icon: e.target.value }))
								}
							/>
						</div>
					</div>
					<div className="form-group">
						<label className="form-label">Color</label>
						<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
							<input
								type="color"
								value={form.color}
								onChange={(e) =>
									setForm((f) => ({ ...f, color: e.target.value }))
								}
								style={{
									width: 44,
									height: 44,
									background: "transparent",
									border: "none",
									cursor: "pointer",
								}}
							/>
							<input
								className="form-input"
								value={form.color}
								onChange={(e) =>
									setForm((f) => ({ ...f, color: e.target.value }))
								}
								style={{ flex: 1 }}
							/>
						</div>
					</div>
					<div
						style={{
							background: "var(--card2)",
							borderRadius: "var(--rs)",
							padding: 14,
							border: `2px solid ${form.color}`,
						}}>
						<span style={{ fontSize: 28 }}>{form.icon}</span>
						<span
							style={{
								fontFamily: "Syne,sans-serif",
								fontWeight: 800,
								fontSize: 16,
								marginLeft: 10,
							}}>
							{form.name || "Preview"}
						</span>
					</div>
				</Modal>
			)}
		</div>
	);
};
